import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import '@polymer/polymer/lib/elements/dom-repeat.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {ResizableBehavior} from '../behaviors/resizable-behavior.js';
import {DraggableBehavior} from '../behaviors/draggable-behavior';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';

class KeliPanel extends ScopeBinding(ResizableBehavior(DraggableBehavior(HotkeyBehavior(PolymerElement)))) {
  static get template() {
    return html`
<style>
  :host {
    --keli-panel-title-height: 34px;
    --layout-fit: {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    };
    display: flex;
    flex-direction: column;
    outline: none;
    position: absolute;
    top: var(--keli-panel-top, 10%);
    left: var(--keli-panel-left, 10%);
    min-height: 100px;
    min-width: 260px;
    width: var(--keli-panel-width, 1000px);
    height: var(--keli-panel-height, 563px);
    background-color: var(--keli-background-color);
    box-shadow: var(--keli-shadow-z1);
    outline: var(--keli-container-border);
    z-index: var(--keli-panel-zindex, var(--keli-zindex-panel-base));
    transform: var(--mission-control-transform, none);
    transform-origin: 0 0;
    transition: transform var(--keli-transition-fast);
  }
  
  :host(.active) {
    box-shadow: var(--keli-shadow-z2);
  }

  :host(.maximized) {
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }

  :host(.minimized) {
    top: auto !important;
    left: var(--keli-panel-minimized-left) !important;
    width: var(--keli-panel-minimized-width, 250px) !important;
    height: var(--keli-panel-title-height) !important;
    bottom: 0 !important;
    box-shadow: none;
    min-height: var(--keli-panel-title-height) !important;
    min-width: auto !important;
    z-index: var(--keli-zindex-panel-minimized) !important;
  }

  :host(.minimized) #menuOptions,
  :host(.minimized) #menu,
  :host(.minimized) #close,
  :host(.minimized) #body,
  :host(.minimized) #configurePanelIcon,
  :host(.minimized) #maximize,
  :host(.minimized) ::slotted(keli-panel-toolbar),
  :host(.minimized) ::slotted(keli-panel-footer) {
    display: none !important;
  }
  
  :host(.closing) #menuOptions,
  :host(.closing) #menu,
  :host(.closing) #configurePanelIcon,
  :host(.closing) #maximize {
    display: none !important;
  }

  :host(.mission-control:hover) {
    outline: 5px solid var(--keli-subtle-active-border);
  }

  :host(.mission-control) #missionControlOverlay {
    display: block;
  }

  #missionControlOverlay {
    display: none;
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: var(--keli-zindex-panel-missioncontroloverlay);
  }

  #missionControlOverlayClose {
    display: inline-block;
    right: 0;
    position: absolute;
    width: 50px;
    height: 50px;
    border-bottom-left-radius: 15%;
    background-color: var(--keli-subtle-background);
    color: var(--keli-text);
  }

  #missionControlOverlayClose sl-icon {
    font-size: 50px;
  }

  #missionControlOverlayClose:hover {
    background-color: var(--keli-subtle-active-background);
  }

  #missionControlOverlayHotkey {
    display: inline-block;
    right: 55px;
    position: absolute;
    width: 50px;
    height: 50px;
    border-bottom-left-radius: 15%;
    background-color: var(--keli-subtle-background);
    color: var(--keli-text);
    font-size: 40px;
    text-align: center;
    text-transform: uppercase;
  }

  :host(.is-dragging) {
    transition: none !important;
  }
  
  :host(.is-dragging) #header, :host(.is-dragging) #title {
    cursor: grabbing;
  }
  
  :host(.hide-tertiary) .tertiary {
    display: none !important;  
  }

  ::slotted(keli-prompt) {
    top: var(--keli-panel-title-height);
  }

  #panelLoadProgress {
    position: absolute;
    width: 100%;
    height: 4px;
    top: var(--keli-panel-title-height);
    z-index: var(--keli-zindex-panel-progress);
    --height: 4px;
    --sl-border-radius-pill: none;
    --indicator-color: var(--keli-progress-color);
  }

  #header {
    height: var(--keli-panel-title-height);
    padding: 0 10px;
    display: flex;
    color: var(--keli-chrome-text-color);
    background-color: var(--keli-theme-grey-500);
    user-select: none;
  }

  :host(.active) #header {
    background-color: var(--keli-theme-primary-800);
  }

  :host(.active.closing) #header {
    background-color: var(--keli-theme-grey-700);
  }

  #title {
    line-height: var(--keli-panel-title-height);
    cursor: default;
    font-weight: 300;
    font-size: 0.9em;
    padding: 0 10px;
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    flex: 1;
    touch-action: none;
    user-select: none;
  }

  .header-options {
    white-space: nowrap;
  }

  .header-options sl-icon {
    cursor: pointer;
    padding: 5px;
    font-size: 24px;
  }

  #panelControlsCreate, #panelControlsDelete, #panelControlsHelp {
    padding: 5px 10px;
    display: none;
  }

  #panelControlsCreate.visible, #panelControlsDelete.visible, #panelControlsHelp.visible {
    display: inline-block;
  }

  .header-options div {
    display: inline-block;
  }

  #body {
    overflow: auto;
    flex: 1;
    display: flex;
    flex-direction: row;
    position: relative;
  }

  #panelPrimaryContent {
    overflow: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  #menu {
    background-color: var(--keli-theme-grey-500);
    --keli-panel-menu-top: var(--keli-panel-title-height);
    --keli-panel-menu-border-color: var(--keli-theme-grey-400);
    --keli-panel-menu-search-background-color: var(--keli-theme-grey-400);
  }

  :host(.active) #menu {
    background-color: var(--keli-theme-primary-800);
    --keli-panel-menu-border-color: var(--keli-theme-primary-600);
    --keli-panel-menu-search-background-color: var(--keli-theme-primary-500);
  }

  /* Resize Handlers */
  .resize-handle {
    position: absolute;
    min-width: 8px;
    min-height: 8px;
    opacity: 0;
    z-index: var(--keli-zindex-panel-resizehandle);
  }

  .resize-corner {
    width: 10px;
    height: 10px;
  }

  #resizeTop {
    cursor: ns-resize;
    top: -3px;
    right: 0;
    left: 0;
  }

  #resizeRight {
    cursor: ew-resize;
    right: -3px;
    top: 0;
    bottom: 0;
  }

  #resizeBottom {
    cursor: ns-resize;
    bottom: -3px;
    right: 0;
    left: 0;
  }

  #resizeLeft {
    cursor: ew-resize;
    left: -3px;
    top: 0;
    bottom: 0;
  }

  #resizeTopLeft {
    cursor: nwse-resize;
    top: -3px;
    left: -3px;
  }

  #resizeTopRight {
    cursor: nesw-resize;
    top: -3px;
    right: -3px;
  }

  #resizeBottomLeft {
    cursor: nesw-resize;
    bottom: -3px;
    left: -3px;
  }

  #resizeBottomRight {
    cursor: nwse-resize;
    bottom: -3px;
    right: -3px;
  }

  #dropToUpload {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: .9;
  }

  #dropToUpload.visible {
    display: block;
  }

  #openAttachmentsContainer {
    display: none;
    position: relative;
    cursor: pointer;
  }

  #openAttachmentsContainer.visible {
    display: inline-block;
  }

  #openAttachmentsCount {
    visibility: hidden;
    position: absolute;
    top: 2px;
    right: 0;
    font-size: .7em;
    border-radius: 5px;
    padding: 1px 3px;
    background-color: var(--keli-theme-accent-900);
  }

  #openAttachmentsCount.visible {
    visibility: visible;
  }
</style>

<div id="missionControlOverlay" on-click="handleMissionControlOverlayClick">
  <template is="dom-if" if="[[shortcutKey]]">
    <span id="missionControlOverlayHotkey">[[shortcutKey]]</span>
  </template>
  <span id="missionControlOverlayClose" on-click="handleMissionControlClosePanel"><sl-icon name="close"></sl-icon></span>
</div>
<div id="resizeLeft" class="resize-handle"></div>
<div id="resizeRight" class="resize-handle"></div>
<div id="resizeTop" class="resize-handle"></div>
<div id="resizeBottom" class="resize-handle"></div>
<div id="resizeTopLeft" class="resize-handle resize-corner"></div>
<div id="resizeTopRight" class="resize-handle resize-corner"></div>
<div id="resizeBottomLeft" class="resize-handle resize-corner"></div>
<div id="resizeBottomRight" class="resize-handle resize-corner"></div>

<header id="header" on-click="handleHeaderClick">
  <div id="menuOptions" class="header-options">
    <sl-icon id="openMenu" name="menu" title="Panel Menu" on-click="handleToggleMenu"></sl-icon>
    <div id="openAttachmentsContainer" on-click="handleOpenAttachments">
      <sl-icon id="openAttachments" name="attachment" title="Attachments ([[attachmentCount]])"></sl-icon>
      <span id="openAttachmentsCount">[[attachmentCount]]</span>
    </div>
    <sl-icon id="panelControlsCreate" class="tertiary" name="add" title="Add a new record" on-click="handlePanelControlsCreateClick"></sl-icon>
    <sl-icon id="panelControlsDelete" class="tertiary" name="remove" title="Remove current record" on-click="handlePanelControlsDeleteClick"></sl-icon>
    <sl-icon id="panelControlsHelp" class="tertiary" name="help-circle-outline" title="View Help" on-click="handlePanelControlsHelpClick"></sl-icon>
  </div>
  <div id="title">[[panelCaption]]</div>
  <div id="panelOptions" class="header-options">
    <template is="dom-if" if="[[showControlConfigurationMenu]]">
      <sl-icon id="configurePanelIcon" class="tertiary" name="settings" title="Configure this Panel" on-click="handleConfigurePanelIconClick"></sl-icon>
    </template>
    <sl-icon id="minimize" name="[[minimizedIcon]]" title="Minimize" on-click="handleMinimizePanel"></sl-icon>
    <sl-icon id="maximize" name="[[maximizedIcon]]" title="Maximize" on-click="handleMaximizePanel"></sl-icon>
    <sl-icon id="close" name="window-close" title="Close" on-click="closePanel"></sl-icon>
  </div>
</header>

<keli-panel-menu id="menu"></keli-panel-menu>
<keli-panel-configuration-menu id="configurePanelContainer" controls="[[allAvailableControls]]"></keli-panel-configuration-menu>

<slot name="placeholder"></slot>
<slot name="toolbar"></slot>
<section id="body">
  <section id="panelPrimaryContent">
    <slot name="fields" id="fields"></slot>
    <slot name="browse"></slot>
    <slot name="full"></slot>
  </section>
  <slot name="sidebar"></slot>
  <template is="dom-if" if="[[hasAttachmentFeature]]">
    <keli-upload id="dropToUpload" keliname="panelupload" is-overlay></keli-upload>
  </template>
</section>
<slot name="footer"></slot>
<slot name="prompt"></slot>`;
  }

  static get is() {
    return 'keli-panel';
  }

  static get properties() {
    // @formatter:off
    return {
      _isInitialized: Boolean,
      active: {type: Boolean, value: true, observer: 'activeChanged'},
      activeElement: {type: Object, observer: 'activeElementChanged'},
      allAvailableControls: {type: Array, value: () => []},
      attachmentCount: {type: Number, scopevar: {name: 'panelattachmentcount'}, observer: 'attachmentCountChanged'},
      caption: {type: String, scopevar: true},
      closing: {type: Boolean, observer: 'closingChanged'},
      contextName: {type: String, scopevar: {name: 'panelcontextname'}},
      controlActivationElements: {type: Array, value: () => []},
      controlActivationIgnoreKeys: {type: Array, value: () => []},
      createdAt: Number,
      desktopElement: {type: Object, observer: 'desktopElementChanged'},
      disableControlConfiguration: {type: Boolean},
      disablePanelMenu: {type: Boolean, value: false, observer: 'disablePanelMenuChanged'},
      dragEnabled: {type: Boolean, value: true, observer: 'dragEnabledChanged'},
      dropToUploadVisible: {type: Boolean, value: false, observer: 'dropToUploadVisibleChanged'},
      hasAttachmentFeature: {type: Number, scopevar: {name: 'panelhasattachmentfeature'}, observer: 'hasAttachmentFeatureChanged'},
      height: {type: Number, value: 563, scopevar: {changesOnly: true, options: {view: true}}, observer: 'heightChanged'},
      inCommandMode: {type: Boolean, value: false, observer: 'inCommandModeChanged'},
      initialControlsCreated: Boolean,
      lastActivatedAt: {type: Number, scopevar: true},
      left: {type: Number, scopevar: {changesOnly: true, options: {view: true}}, observer: 'leftChanged'},
      maximized: {type: Boolean, scopevar: {options: {view: true}}, observer: 'maximizedChanged'},
      maximizedForced: {type: Boolean},
      maximizedIcon: {type: String, computed: 'computeMaximizedIcon(maximized, _isInitialized)'},
      minimized: {type: Boolean, observer: 'minimizedChanged'},
      minimizedIcon: {type: String, computed: 'computeMinimizedIcon(minimized, _isInitialized)'},
      missionControl: {type: Boolean, observer: 'missionControlChanged'},
      overlayContainers: {type: Array, value: () => []},
      panelCaption: {type: String, computed: 'computePanelCaption(caption, contextName, closing, _isInitialized)'},
      panelControlCreateVisible: {type: Boolean, scopevar: {name: 'panelcontrolcreatevisible'}, observer: 'panelControlCreateVisibleChanged'},
      panelControlDeleteVisible: {type: Boolean, scopevar: {name: 'panelcontroldeletevisible'}, observer: 'panelControlDeleteVisibleChanged'},
      panelControlHelpVisible: {type: Boolean, scopevar: {name: 'panelcontrolhelpvisible'}, observer: 'panelControlHelpVisibleChanged'},
      progressBarElement: Object,
      resizeEnabled: {type: Boolean, value: true, observer: 'resizeEnabledChanged'},
      saveAndCloseHotkeyEnabled: {type: Boolean, value: true},
      shortcutKey: String,
      showControlConfigurationMenu: {type: Boolean, computed: 'computeShowControlConfigurationMenu(disableControlConfiguration, allAvailableControls.length)'},
      top: {type: Number, scopevar: {changesOnly: true, options: {view: true}}, observer: 'topChanged'},
      useCommandMode: {type: Boolean, scopevar: {name: 'commandmode'}, observer: 'useCommandModeChanged'},
      width: {type: Number, value: 1000, scopevar: {changesOnly: true, options: {view: true}}, observer: 'widthChanged'},
      panelControlsDataSettled: {type: Object, value:  () => ({})}
    }
    // @formatter:on
  }

  connectedCallback() {
    super.connectedCallback();
    this._isInitialized = true;
    this._ensureAttribute('tabindex', '0');
    this.createdAt = Date.now();

    // Set some custom behavior for smaller devices.
    if (document.documentElement.clientWidth <= 700) {
      this.maximizedForced = true;
      this.classList.toggle('maximized', true);
      this.resizeEnabled = false;
      this.dragEnabled = false;
      this.$.maximize.style.display = 'none';
    }

    this.dispatchEvent(new CustomEvent('keliPanelActivated', {
      bubbles: true, composed: true,
      detail: {element: this, isConnecting: true}
    }));

    this.addEventListener('deRegisterOverlayContainerWithPanel', this.handleDeRegisterOverlayContainerWithPanel);
    this.addEventListener('click', this.handleSetPanelAsActive);
    this.addEventListener('dragenter', this.handleDragEnter);
    this.addEventListener('focus', this.handleSetPanelAsActive);
    this.addEventListener('focusActiveControl', this.handleFocusActiveControl);
    this.addEventListener('panel-controller-visibility', this.handlePanelControllerVisibility);
    this.addEventListener('panelElementAttributeChanged', this.handlePanelElementAttributeChanged);
    this.addEventListener('panelLoadingProgress', this.handlePanelLoadingProgress);
    this.addEventListener('registerOverlayContainerWithPanel', this.handleRegisterOverlayContainerWithPanel);
    this.addEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);
    this.addEventListener('scrollElement', this.handleScrollElement);
    this.addEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.addEventListener('setPanelAsActive', this.handleSetPanelAsActive);
    this.addEventListener('setPanelAsInactive', this.handleSetPanelAsInactive);
    this.addEventListener('unregisterPanelHotkey', this.handleUnRegisterPanelHotkey);
    this.addEventListener('uploadDraggingEnded', this.handleDragEnd);
    this.addEventListener('browseDataSettledChanged', this.handleBrowseDataSettled);

    this.resizableParentElement = this;
    this.resizableHandleElements = [
      {element: this.$.resizeTop, direction: 'n'},
      {element: this.$.resizeRight, direction: 'e'},
      {element: this.$.resizeBottom, direction: 's'},
      {element: this.$.resizeLeft, direction: 'w'},
      {element: this.$.resizeTopLeft, direction: 'nw'},
      {element: this.$.resizeTopRight, direction: 'ne'},
      {element: this.$.resizeBottomLeft, direction: 'sw'},
      {element: this.$.resizeBottomRight, direction: 'se'}
    ];

    this.draggableFuzzyContainment = {right: 200, bottom: 50, left: 200};
    this.draggableHandleElement = this.$.title;
    this.draggableStartHandler = () => {
      this.active = true;
    };
    this.draggableMoveHandler = () => {
      if (this.overlayContainers) {
        this.overlayContainers.forEach(function (overlay) {
          overlay.dispatchEvent(new CustomEvent('panelDragMove', {
            bubbles: true, composed: true,
            detail: {element: this}
          }));
        });
      }
    };
    this.draggableEndHandler = (position) => {
      this.style.top = this.style.left = '';
      this.panelPositionSave({top: position.newY, left: position.newX});
    };

    let skipFieldStartedElement;
    this.AdjacentElement = new AdjacentElement({
      'this': this,
      'selector': '.input-field, .focusable-control, keli-button',
      'properties': {disabled: false, hidden: false, createdFromPanelController: true, removeFromPanelTabOrder: false},
      'wrap': true,
      'defaultCallback': (newElement, numericDirection) => {
        if (newElement && newElement.skip === true && numericDirection === 1 && skipFieldStartedElement !== newElement) {
          skipFieldStartedElement = skipFieldStartedElement || newElement;
          return this.AdjacentElement.next(newElement);
        }
        skipFieldStartedElement = undefined;

        if (!this.active || !newElement || !newElement.dispatchEvent || this.panelPromptExists()) {
          return;
        }
        if (newElement === this.activeElement && !this.forceNextFocusActiveControl) {
          // We check numericDirection to make sure the user was trying to move, not refocus the current.
          if (numericDirection && newElement._keliAccept) {
            newElement._keliAccept();
          }
          return;
        }

        this.forceNextFocusActiveControl = false;
        newElement.dispatchEvent(new CustomEvent('focusElement', {
          bubbles: true, composed: true,
          detail: {
            element: newElement,
            first: (numericDirection === 1),
            last: (numericDirection === -1),
          }
        }));
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.desktopElement.dispatchEvent(new CustomEvent('keliPanelClosed', {
      bubbles: true, composed: true,
      detail: {element: this}
    }));

    this.activeElement = null;
    this.allAvailableControls = null;
    this.controlActivationElements = null;
    this.desktopElement = null;
    this.overlayContainers = null;
    this.progressBarElement = null;

    if (this.AdjacentElement) {
      this.AdjacentElement.destroy();
      this.AdjacentElement = null;
    }

    this.removeEventListener('deRegisterOverlayContainerWithPanel', this.handleDeRegisterOverlayContainerWithPanel);
    this.removeEventListener('click', this.handleSetPanelAsActive);
    this.removeEventListener('dragenter', this.handleDragEnter);
    this.removeEventListener('focus', this.handleSetPanelAsActive);
    this.removeEventListener('focusActiveControl', this.handleFocusActiveControl);
    this.removeEventListener('panel-controller-visibility', this.handlePanelControllerVisibility);
    this.removeEventListener('panelElementAttributeChanged', this.handlePanelElementAttributeChanged);
    this.removeEventListener('panelLoadingProgress', this.handlePanelLoadingProgress);
    this.removeEventListener('registerOverlayContainerWithPanel', this.handleRegisterOverlayContainerWithPanel);
    this.removeEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);
    this.removeEventListener('scrollElement', this.handleScrollElement);
    this.removeEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.removeEventListener('setPanelAsActive', this.handleSetPanelAsActive);
    this.removeEventListener('setPanelAsInactive', this.handleSetPanelAsInactive);
    this.removeEventListener('unregisterPanelHotkey', this.handleUnRegisterPanelHotkey);
    this.removeEventListener('uploadDraggingEnded', this.handleDragEnd);
    this.removeEventListener('browseDataSettledChanged', this.handleBrowseDataSettled);
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);

    this.dispatchEvent(new CustomEvent('keliPanelOpened', {
      bubbles: true, composed: true,
      detail: {element: this, scopeid: this.getKeliScopeId(), timestamp: Date.now()}
    }));

    // Set lastActivatedAt back to Keli for the initial load.
    this.executeOnKeliReadyComplete(() => {
      this.lastActivatedAt = Date.now();
    });
  }

  computeMaximizedIcon(maximized) {
    return (maximized ? 'fullscreen-exit' : 'fullscreen');
  }

  computeMinimizedIcon(minimized) {
    return (minimized ? 'window-restore' : 'chevron-down');
  }

  computePanelCaption(caption, contextName, closing) {
    if (closing) {
      return 'Closing…';
    }

    const parts = [
      caption,
      contextName
    ];
    return parts.filter(part => part).join(' • ');
  }

  computeShowControlConfigurationMenu(disableControlConfiguration, allAvailableControlsLength) {
    if (disableControlConfiguration) {
      return false;
    }
    return (allAvailableControlsLength > 0);
  }

  computeStylingFromWidth(width) {
    if (width) {
      this.classList.toggle('hide-tertiary', width < 350);
    }
  }

  activeChanged(newValue) {
    this.classList.toggle('active', newValue);
    this.resetHotkeyState();

    if (newValue) {
      this.lastActivatedAt = Date.now();
      if (this.initialControlsCreated) {
        this.dispatchEvent(new CustomEvent('focusActiveControl'));
      }
      this.dispatchEvent(new CustomEvent('keliPanelActivated', {
        bubbles: true, composed: true,
        detail: {element: this}
      }));
      this.setHotkeyListenerState(this._defaultListenerName, true);
    } else {
      this.activeElement = undefined;
      this.setHotkeyListenerState(this._defaultListenerName, false);
    }
  }

  activeElementChanged(newValue) {
    if (this.overlayContainers) {
      this.overlayContainers.forEach(function (overlay) {
        if (overlay.element !== newValue && overlay.visible()) {
          overlay.hide();
        }
      });
    }
    this.resetHotkeyState();
  }

  attachmentCountChanged(newValue) {
    this.$.openAttachmentsCount.classList.toggle('visible', (newValue > 0));
  }

  closingChanged(newValue) {
    this.classList.toggle('closing', newValue);
  }

  desktopElementChanged(newValue) {
    this.draggableContainmentElement = newValue;
  }

  disablePanelMenuChanged(newValue) {
    if (newValue) {
      this.$.openMenu.remove();
      this.$.menu.remove();
    }
  }

  dragEnabledChanged(newValue) {
    this.draggableEnabled = newValue;
  }

  dropToUploadVisibleChanged(newValue) {
    const uploadElement = this.shadowRoot.querySelector('#dropToUpload');
    if (uploadElement) {
      uploadElement.classList.toggle('visible', newValue);
    }
  }

  hasAttachmentFeatureChanged(newValue) {
    this.$.openAttachmentsContainer.classList.toggle('visible', newValue);
  }

  inCommandModeChanged(newValue) {
    if (newValue) {
      this.activeElement = this.AdjacentElement.element = undefined;
      if (this.active) {
        this.focus();
      }
    } else {
      this.dispatchEvent(new CustomEvent('focusActiveControl'));
    }

    this.assignControlActivationKeys();
  }

  maximizedChanged(newValue) {
    if (this.maximizedForced && newValue !== true) {
      return;
    }
    this.classList.toggle('maximized', newValue);
    this.resizeEnabled = !newValue;
    this.dragEnabled = !newValue;
  }

  minimizedChanged(newValue) {
    this.classList.toggle('minimized', newValue);
    this.dispatchEvent(new CustomEvent('minimizePanel', {
      bubbles: true, composed: true,
      detail: {element: this}
    }));

    this.resizeEnabled = !newValue;
    this.dragEnabled = !newValue;
    if (!newValue) {
      this.handleSetPanelAsActive();
    }
  }

  missionControlChanged(newValue) {
    this.classList.toggle('mission-control', newValue);
  }

  panelControlCreateVisibleChanged(newValue) {
    this.$.panelControlsCreate.classList.toggle('visible', newValue);
  }

  panelControlDeleteVisibleChanged(newValue) {
    this.$.panelControlsDelete.classList.toggle('visible', newValue);
  }

  panelControlHelpVisibleChanged(newValue) {
    this.$.panelControlsHelp.classList.toggle('visible', newValue);
  }

  resizeEnabledChanged(newValue) {
    this.resizableEnabled = newValue;
  }

  useCommandModeChanged(newValue) {
    if (!KeliInterface.isTouchDevice) {
      this.inCommandMode = newValue;
    }
  }

  handleConfigurePanelIconClick(event) {
    event.preventDefault();
    this.$.configurePanelContainer.panel = this;
    this.$.configurePanelContainer.fromElement = getEventTarget(event);
    this.$.configurePanelContainer.opened = !this.$.configurePanelContainer.opened;
  }

  handleDeRegisterOverlayContainerWithPanel(event) {
    if (this.overlayContainers) {
      this.overlayContainers = this.overlayContainers.filter(overlay => {
        return (overlay !== event.detail.container);
      });
    }
  }

  handleSetPanelAsActive() {
    if (!this.missionControl && !this.minimized) {
      this.active = true;
    }
  }

  handleDragEnter(event) {
    if (this.hasAttachmentFeature) {
      let transfer = event.dataTransfer;
      if (transfer.types && (transfer.types.includes('Files') || transfer.types.includes('application/x-moz-file'))) {
        this.dropToUploadVisible = true;
      }
    }
  }

  handleFocusActiveControl(event) {
    if (!this.AdjacentElement || !this.active || this.panelPromptExists() || this.missionControl) {
      return;
    }

    if (this.$.menu?.opened) {
      this.$.menu.focusSearch();
    } else if (this.inCommandMode) {
      this.focus();
    } else if (this.AdjacentElement) {
      if (event && event.detail && event.detail.globalFocus) {
        this.forceNextFocusActiveControl = true;
      }
      this.AdjacentElement.current();
      if (!this.AdjacentElement.element && !KeliInterface.isTouchDevice) {
        this.focus();
      }
    }
  }

  handleHeaderClick() {
    if (this.minimized) {
      this.minimized = false;
    }
  }

  handleMaximizePanel(event) {
    event.stopPropagation();
    this.maximized = !this.maximized;
  }

  handleMinimizePanel(event) {
    event.stopPropagation();
    this.minimized = !this.minimized;
  }

  handleMissionControlClosePanel(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.closePanel(event);
  }

  handleMissionControlOverlayClick() {
    this.dispatchEvent(new CustomEvent('selectMissionControlPanel', {
      bubbles: true, composed: true,
      detail: {element: this}
    }));
  }

  handleOpenAttachments(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('openPanelAttachments', {bubbles: true, composed: true}));
  }

  handlePanelControllerVisibility() {
    this.assignControlActivationKeys();
  }

  handlePanelControlsCreateClick(event) {
    event.stopPropagation();

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    this.dispatchEvent(new CustomEvent('panelControlCreate', {bubbles: true, composed: true}));
  }

  handlePanelControlsDeleteClick(event) {
    // This only needs to factor in if it is in browse mode.
    if (this.panelControlsDataSettled['browse'] === false) {
      return;
    }

    event.stopPropagation();

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    this.dispatchEvent(new CustomEvent('panelControlDelete', {bubbles: true, composed: true}));
  }

  handlePanelControlsHelpClick(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('panelControlHelp', {bubbles: true, composed: true}));
  }

  handlePanelElementAttributeChanged(event) {
    if (!event || !event.detail) {
      return;
    }
    switch (event.detail.attribute) {
      case 'readOnly':
        if (event.detail.element === this.activeElement && event.detail.element.readOnly === true) {
          this.AdjacentElement.next(this.activeElement);
        }
        break;
      case 'skip':
        if (event.detail.element === this.activeElement && event.detail.element.skip === true) {
          this.AdjacentElement.next(this.activeElement);
        }
        break;
      case 'hotkey':
      case 'disabled':
        this.assignControlActivationKeys();
        break;
    }
  }

  handlePanelLoadingProgress(event) {
    if (!this.progressBarElement && (event?.detail.progress || 0) < 100) {
      this.progressBarElement = document.createElement('sl-progress-bar');
      this.progressBarElement.setAttribute('id', 'panelLoadProgress');
      this.shadowRoot.appendChild(this.progressBarElement);
    }

    if (this.progressBarElement && event?.detail) {
      if (event.detail.indeterminate !== undefined) {
        this.progressBarElement.indeterminate = event.detail.indeterminate;
      }
      if (event.detail.progress !== undefined) {
        this.progressBarElement.value = parseInt(event.detail.progress, 10);
      }
    }

    if (this.progressBarElement && this.progressBarElement.value === 100) {
      this.progressBarElement.remove();
      this.progressBarElement = undefined;
    }
  }

  handleRegisterOverlayContainerWithPanel(event) {
    event.detail.container.panel = this;
    this.overlayContainers && this.overlayContainers.push(event.detail.container);
  }

  handleRegisterPanelHotkey(event) {
    if (KeliInterface.Hotkeys.alphaNumerics.includes(event.detail.keys)) {
      return;
    }

    // If we are registering the standard Keli F12 hotkey, prevent it on the panel.
    if (event.detail.keys === KeliInterface.Hotkeys.panel.saveAndClose.keys || (Array.isArray(event.detail.keys) && event.detail.keys.includes(KeliInterface.Hotkeys.panel.saveAndClose.keys))) {
      this.saveAndCloseHotkeyEnabled = false;
    }

    const autoControlMatch = event.detail.keys.match(/^alt ([a-z0-9])$/i);
    if (autoControlMatch) {
      this.controlActivationIgnoreKeys.push(autoControlMatch.at(1));
      this.assignControlActivationKeys();
    }

    const hotkeyFunction = async (hotkeyEvent) => {
      // Manually fire _keliAccept on the panel to ensure fields are saved.
      if (this.activeElement && this.activeElement._keliAccept) {
        await this.activeElement._keliAccept();
      }
      if (event.detail.action) {
        event.detail.action(hotkeyEvent);
      } else {
        event.detail.scope.activate(hotkeyEvent);
      }
    };

    this.registerHotkey({
      'keys': event.detail.keys,
      'this': event.detail.scope || undefined,
      'is_unordered': true,
      'prevent_repeat': true
    }, {
      down: hotkeyFunction
    });
  }

  handleResizableResizeElement(event) {
    event.stopPropagation();

    if (event.detail.top !== undefined) {
      this.style.top = `${event.detail.top > 0 ? event.detail.top : 0}px`;
    }
    if (event.detail.left !== undefined) {
      this.style.left = `${event.detail.left}px`;
    }
    if (event.detail.width !== undefined) {
      this.style.width = `${event.detail.width}px`;
      this.computeStylingFromWidth(event.detail.width);
    }
    if (event.detail.height !== undefined) {
      this.style.height = `${event.detail.height}px`;
    }

    if (this.overlayContainers) {
      this.overlayContainers.forEach(overlay => {
        overlay.dispatchEvent(new CustomEvent('resizeElement', {detail: {element: this}}));
      });
    }
  }

  handleResizableResizeElementEnded(event) {
    event.stopPropagation();
    this.style.top = this.style.left = this.style.width = this.style.height = '';
    this.panelPositionSave(event.detail);

    if (this.overlayContainers) {
      this.overlayContainers.forEach(overlay => {
        overlay.dispatchEvent(new CustomEvent('resizeElement', {detail: {element: this}}));
      });
    }
  }

  handleScrollElement() {
    if (this.overlayContainers) {
      this.overlayContainers.forEach(overlay => {
        overlay.dispatchEvent(new CustomEvent('scrollElement', {detail: {element: this}}));
      });
    }
  }

  handleSetPanelActiveElement(event) {
    if (!this.AdjacentElement || this.panelPromptExists() || this.missionControl) {
      return;
    }

    if (event.detail.previous) {
      return this.AdjacentElement.previous(event.detail.element);
    }
    if (event.detail.next) {
      return this.AdjacentElement.next(event.detail.element);
    }

    this.activeElement = this.AdjacentElement.element = event.detail.element;
    this.inCommandMode = false;
    this.active = true;
    if (this.$.menu?.opened) {
      this.$.menu.opened = false;
    }
  }

  handleSetPanelAsInactive() {
    this.active = false;
  }

  handleToggleMenu() {
    this.$.menu.toggleElement = this.$.openMenu;
    this.$.menu.toggleMenu();
  }

  handleUnRegisterPanelHotkey(event) {
    if (event.detail.keys === KeliInterface.Hotkeys.panel.saveAndClose.keys || event.detail.keys.includes(KeliInterface.Hotkeys.panel.saveAndClose.keys)) {
      this.saveAndCloseHotkeyEnabled = true;
    }
    this.unregisterHotkey({keys: event.detail.keys});
  }

  handleDragEnd() {
    if (this.hasAttachmentFeature) {
      this.dropToUploadVisible = false;
    }
  }

  handleBrowseDataSettled({detail}) {
    this.panelControlsDataSettled[detail.control] = detail.settled;

    const actions = [
      ...this.querySelector('keli-panel-toolbar')?.querySelectorAll(`keli-button[depends-on-browse="${detail.control}"]`) ?? [],
      ...this.querySelector('keli-panel-fields')?.querySelectorAll(`keli-button[depends-on-browse="${detail.control}"]`) ?? []
    ];
    for (const action of actions) {
      action.browseSettled = detail.settled;
    }
    this.$.menu.updateBrowseSettled(detail.control, detail.settled);
  }

  assignControlActivationKeys() {
    if (!this.AdjacentElement) {
      return;
    }

    this._assignControlActivationKeysJob = Debouncer.debounce(this._assignControlActivationKeysJob, timeOut.after(20), () => {
      if (!this.AdjacentElement) {
        return;
      }
      const toolbarButtons = this.querySelector('keli-panel-toolbar').querySelectorAll('keli-button:not([hidden])');
      const panelElements = this.AdjacentElement.getAllElements(`${this.AdjacentElement.options.selector}, keli-value`);

      const sortedElements = [];
      const fieldElements = [];
      sortedElements.push(...toolbarButtons);
      for (const element of panelElements) {
        if (['keli-browse', 'keli-button'].includes(element.tagName.toLowerCase())) {
          sortedElements.push(element);
        } else if (element.hotkey && (element.hotkey === true || KeliInterface.Hotkeys.alphaNumerics.includes(element.hotkey.toString().toLowerCase()))) {
          fieldElements.push(element);
        }
      }
      sortedElements.push(...fieldElements);

      let keysAvailable = KeliInterface.Hotkeys.alphaNumerics.slice(0, 36).filter(key => {
        return !this.controlActivationIgnoreKeys.includes(key);
      });

      // Loop through elements with assigned hotkeys, and use those first.
      for (let element of sortedElements) {
        if (!keysAvailable.length) {
          break;
        }

        const elementKeys = (element.hotkeys ? element.hotkeys : (element.hotkey ? [element.hotkey] : undefined));
        if (elementKeys?.length) {
          for (let hotkey of elementKeys) {
            if (keysAvailable.includes(hotkey)) {
              keysAvailable = keysAvailable.filter(key => key !== hotkey);
              element._controlActivationAssignedHotkey = hotkey;
              break;
            }
          }
        }
      }

      // Use the rest of the available hotkeys.
      const newElements = [];
      for (let element of sortedElements) {
        if (!keysAvailable.length) {
          break;
        }

        let finalHotkey;
        if (element._controlActivationAssignedHotkey) {
          // Control has been manually assigned a single-key hotkey.
          finalHotkey = element._controlActivationAssignedHotkey;
        } else {
          const consumedKey = keysAvailable.shift();

          // If the control already has a hotkey, only assign numbers.
          if (element.hotkeys || element.hotkey) {
            if (KeliInterface.Hotkeys.alphaNumerics.slice(0, 10).includes(consumedKey)) {
              finalHotkey = consumedKey;
            }
          } else {
            finalHotkey = consumedKey;
          }
        }

        if (finalHotkey) {
          element.shortcutKey = (this.inCommandMode ? '' : 'alt ') + finalHotkey;
          newElements.push(element);
        }
      }

      const oldElements = this.controlActivationElements.filter(element => !newElements.includes(element));
      for (const element of oldElements) {
        element.shortcutKey = undefined;
      }
      this.controlActivationElements = newElements;
    });
  }

  closePanel(event) {
    if (this.$.menu?.opened) {
      this.$.menu.opened = false;
      return;
    }
    if (!this.saveAndCloseHotkeyEnabled && event && event.key && event.key.toLowerCase() === KeliInterface.Hotkeys.panel.saveAndClose.keys) {
      return;
    }

    let prompt = this.panelPromptExists();
    if (prompt) {
      prompt.close();
      return;
    }

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    // Give other event handlers a chance to finish before closing the panel
    afterNextRender(this, () => {
      this.dispatchEvent(new CustomEvent('closePanel', {
        bubbles: true,
        composed: true,
        detail: {element: this, event: event}
      }));
    });
  }

  panelPositionInitialize(position) {
    const updateObject = {};
    if (position.top !== undefined) {
      updateObject['--keli-panel-top'] = `${position.top > 0 ? position.top : 0}px`;
    }
    if (position.left !== undefined) {
      updateObject['--keli-panel-left'] = `${position.left}px`;
    }
    if (position.width !== undefined) {
      updateObject['--keli-panel-width'] = `${position.width}px`;
      this.computeStylingFromWidth(position.width);
    }
    if (position.height !== undefined) {
      updateObject['--keli-panel-height'] = `${position.height}px`;
    }
    this.updateStyles(updateObject);

    if (position.maximized !== undefined) {
      this.maximized = position.maximized;
    }
  }

  panelPositionSave(position = {}) {
    if (position.top !== undefined) {
      this.top = position.top;
    }
    if (position.left !== undefined) {
      this.left = position.left;
    }
    if (position.width !== undefined) {
      this.width = position.width;
    }
    if (position.height !== undefined) {
      this.height = position.height;
    }
  }

  topChanged(newValue) {
    if (this.offsetTop !== newValue && !isNaN(newValue)) {
      if (newValue < 0) {
        newValue = 0;
      }
      this.updateStyles({'--keli-panel-top': `${newValue}px`});
    }
  }

  leftChanged(newValue) {
    if (this.offsetLeft !== newValue && !isNaN(newValue)) {
      this.updateStyles({'--keli-panel-left': `${newValue}px`});
    }
  }

  widthChanged(newValue) {
    if (this.offsetWidth !== newValue && !isNaN(newValue)) {
      this.updateStyles({'--keli-panel-width': `${newValue}px`});
    }
  }

  heightChanged(newValue) {
    if (this.offsetHeight !== newValue && !isNaN(newValue)) {
      this.updateStyles({'--keli-panel-height': `${newValue}px`});
    }
  }

  panelPromptExists() {
    let prompt = this.querySelector('keli-prompt');
    if (prompt && !prompt.closing) {
      return prompt;
    }
    return false;
  }
}

customElements.define(KeliPanel.is, KeliPanel);
export {KeliPanel};
